import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Moment } from 'moment-timezone';
import { fetchAlertsThunk, NewBookingsStatus } from '@spike/new-bookings-action';
import { NewBookingAlert } from '@spike/new-booking-model';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { SessionAddressOverride } from 'model/NewMultipleBooking';
import { OUTSIDE_BUSINESS_HOURS_ALERT, RoutedBookingAlerts, routeBookingAlerts } from './bookingAlerts';

interface BookingAlertsInput {
    clientId: number;
    petId: number;
    from?: Moment;
    to?: Moment;
    serviceId?: number;
    staffId?: number;
    appointmentId?: number;
    addressOverride?: SessionAddressOverride;
    // Off when nothing books the typed time, e.g. every selected date has a session picked.
    enabled?: boolean;
}

export interface BookingAlerts {
    routedAlerts: RoutedBookingAlerts;
    dateTimeAlerts: Array<NewBookingAlert>;
}

const hasDateTimeAlert = (alertList?: Array<NewBookingAlert>): boolean =>
    Boolean(alertList?.some(alert => alert.alertType === 'date_time'));

export const useBookingAlerts = ({
    clientId,
    petId,
    from,
    to,
    serviceId,
    staffId,
    appointmentId,
    addressOverride,
    enabled = true
}: BookingAlertsInput): BookingAlerts => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const newBookingStatus = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);
    const alerts = useSelector<RootState, Array<NewBookingAlert> | undefined>(state => state.newBookings.alerts);
    const [alertCheckPhase, setAlertCheckPhase] = useState<'idle' | 'checking_start' | 'checking_end'>('idle');
    const [startAlerts, setStartAlerts] = useState<Array<NewBookingAlert>>([]);
    const [endOutsideBusinessHours, setEndOutsideBusinessHours] = useState(false);

    useEffect(() => {
        if (!enabled || !from?.isValid() || !to?.isValid() || !clientId || !petId) {
            setAlertCheckPhase('idle');
            setStartAlerts([]);
            setEndOutsideBusinessHours(false);
            return;
        }

        setStartAlerts([]);
        setEndOutsideBusinessHours(false);
        setAlertCheckPhase('checking_start');

        dispatch(
            fetchAlertsThunk(
                apiClientWrapper,
                clientId,
                petId,
                from.clone().tz('utc'),
                false,
                serviceId,
                staffId,
                appointmentId,
                addressOverride
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper is unstable by design
    }, [enabled, from, to, serviceId, staffId, clientId, petId, appointmentId, addressOverride]);

    // The alerts slice keeps only the last response, so the start-time alerts are copied
    // out before the end-time request overwrites them.
    useEffect(() => {
        if (newBookingStatus !== NewBookingsStatus.FetchAlertsSuccess) return;

        if (alertCheckPhase === 'checking_start') {
            setStartAlerts(alerts ?? []);
            setAlertCheckPhase('checking_end');

            if (to?.isValid() && clientId && petId) {
                dispatch(
                    fetchAlertsThunk(
                        apiClientWrapper,
                        clientId,
                        petId,
                        to.clone().tz('utc'),
                        false,
                        serviceId,
                        staffId,
                        appointmentId,
                        addressOverride
                    )
                );
            } else {
                setAlertCheckPhase('idle');
            }
            return;
        }

        if (alertCheckPhase === 'checking_end') {
            setEndOutsideBusinessHours(hasDateTimeAlert(alerts));
            setAlertCheckPhase('idle');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper is unstable by design
    }, [newBookingStatus, alertCheckPhase, alerts, to, serviceId, staffId, clientId, petId, appointmentId]);

    const routedAlerts = useMemo(() => routeBookingAlerts(startAlerts), [startAlerts]);

    const dateTimeAlerts = useMemo(
        () =>
            routedAlerts.dateTime.length > 0
                ? routedAlerts.dateTime
                : endOutsideBusinessHours
                ? [OUTSIDE_BUSINESS_HOURS_ALERT]
                : [],
        [routedAlerts.dateTime, endOutsideBusinessHours]
    );

    return { routedAlerts, dateTimeAlerts };
};

export default useBookingAlerts;
